import { api } from '../api/apiClient';

export const DIAGRAM_MAX_FILE_BYTES = 8 * 1024 * 1024;
export const DIAGRAM_MAX_BASE64_CHARS = 7 * 1024 * 1024;
export const DIAGRAM_MAX_DIMENSION = 1600;
const ACCEPTED_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp'];

export const getImageExtension = (filename = '') => {
  const lower = String(filename).toLowerCase();
  const idx = lower.lastIndexOf('.');
  return idx >= 0 ? lower.slice(idx) : '';
};

export const isAcceptedDiagramFile = (file) => {
  if (!file) return false;
  if (file.size > DIAGRAM_MAX_FILE_BYTES) return false;
  return ACCEPTED_IMAGE_EXTENSIONS.includes(getImageExtension(file.name));
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ''));
  reader.onerror = () => reject(new Error('read_error'));
  reader.readAsDataURL(file);
});

const loadImage = (dataUrl) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('read_error'));
  img.src = dataUrl;
});

/**
 * Downscale an image client-side (canvas) so the base64 payload stays small.
 * @returns {Promise<{ mimeType: string, data: string }>} base64 without data: prefix
 */
export const downscaleDiagramImage = async (file) => {
  if (!isAcceptedDiagramFile(file)) {
    throw new Error(ACCEPTED_IMAGE_EXTENSIONS.includes(getImageExtension(file?.name)) ? 'too_big' : 'bad_type');
  }
  const originalUrl = await fileToDataUrl(file);
  const img = await loadImage(originalUrl);
  const scale = Math.min(1, DIAGRAM_MAX_DIMENSION / Math.max(img.width, img.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(img.width * scale));
  canvas.height = Math.max(1, Math.round(img.height * scale));
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  // JPEG keeps diagrams small; keep PNG only when originally small transparency matters
  const mimeType = 'image/jpeg';
  const dataUrl = canvas.toDataURL(mimeType, 0.85);
  const data = dataUrl.split(',')[1] || '';
  if (!data) throw new Error('read_error');
  if (data.length > DIAGRAM_MAX_BASE64_CHARS) throw new Error('too_big');
  return { mimeType, data };
};

/**
 * Call backend diagram extraction. Returns the raw data payload
 * { assets, findings, techniques, droppedTechniques, droppedAssets, provider, fallback, ... }.
 */
export const requestNetworkDiagramExtraction = async ({ analysisId, filename, image, businessAssets }) => {
  const response = await api.post('/ai/extract-network-diagram', {
    analysisId,
    filename,
    imageData: image.data,
    mimeType: image.mimeType,
    businessAssets: (businessAssets || []).map((a) => ({
      id: String(a.id),
      name: a.name,
      vendor: a.vendor,
      product: a.product,
      version: a.version,
      type: a.type || a.nature,
    })),
  });
  if (!response?.success) {
    throw new Error(response?.message || 'extraction_failed');
  }
  return response.data;
};
